import React, { useState, useEffect } from "react";
import api from "../services/api";
import { format } from "date-fns";
import {
  FiDownload,
  FiFilter,
  FiCheckCircle,
  FiXCircle,
  FiClock,
} from "react-icons/fi";

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    provider: "",
  });

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, filters]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        ...filters,
      });
      const response = await api.get(`/transactions?${params}`);
      setTransactions(response.data.data || []);
      setPagination((prev) => ({ ...prev, ...response.data.pagination }));
    } catch (err) {
      console.error("Failed to load transactions", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/transactions/export", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `transactions-${format(new Date(), "yyyy-MM-dd")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Failed to export transactions");
    }
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "succeeded":
        return <FiCheckCircle color="var(--success)" />;
      case "failed":
        return <FiXCircle color="var(--error)" />;
      case "pending":
      case "processing":
        return <FiClock color="var(--warning)" />;
      default:
        return <FiClock color="var(--text-secondary)" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      succeeded: "badge-success",
      failed: "badge-error",
      pending: "badge-warning",
      processing: "badge-info",
      refunded: "badge-warning",
    };
    return badges[status] || "badge-info";
  };

  return (
    <div>
      <div className="page-header flex-between">
        <div>
          <h1 className="page-title">Transactions</h1>
          <p className="page-subtitle">View your payment history</p>
        </div>
        <button className="btn btn-secondary" onClick={handleExport}>
          <FiDownload /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4" style={{ marginBottom: "24px" }}>
        <div className="flex gap-4" style={{ display: "flex", gap: "16px" }}>
          <select
            className="form-input"
            style={{ width: "auto" }}
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
            <option value="refunded">Refunded</option>
          </select>

          <select
            className="form-input"
            style={{ width: "auto" }}
            value={filters.provider}
            onChange={(e) =>
              setFilters({ ...filters, provider: e.target.value })
            }
          >
            <option value="">All Providers</option>
            <option value="stripe">Stripe</option>
            <option value="mercadopago">Mercado Pago</option>
            <option value="btcpay">Bitcoin</option>
          </select>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : transactions.length > 0 ? (
          <>
            <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Provider</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>
                      {format(new Date(tx.created_at), "MMM d, yyyy HH:mm")}
                    </td>
                    <td>{tx.product_name || "Payment"}</td>
                    <td style={{ fontWeight: 600 }}>
                      {formatCurrency(tx.amount, tx.currency)}
                    </td>
                    <td style={{ textTransform: "capitalize" }}>
                      {tx.provider}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(tx.status)}`}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Pagination */}
            <div className="flex-between mt-4" style={{ marginTop: "16px" }}>
              <span className="text-muted">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <div
                className="flex gap-2"
                style={{ display: "flex", gap: "8px" }}
              >
                <button
                  className="btn btn-outline"
                  disabled={pagination.page <= 1}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                >
                  Previous
                </button>
                <button
                  className="btn btn-outline"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <p className="text-muted">No transactions found</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Transactions;
